import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import { Field, Select, LinesField, ImageField, ListEditor } from "./fields";
import { updatePage, resetPage, formatApiError } from "@/lib/api";
import { MagazinePage, PAGE_W, PAGE_H } from "@/components/magazine/MagazinePage";
import { ReaderContext } from "@/components/magazine/ReaderContext";
import { EDITION_SLUG } from "@/hooks/useEdition";

const TEMPLATES = ["cover", "ad", "contents", "letter", "portrait", "opener", "section_opener", "interview", "article", "essay", "diary", "cards", "list", "closing"];
const SECTIONS = ["cover", "music", "screen", "influence", "style", "culture", "next", "legacy", "list"];
const KINDS = ["watch", "listen", "visit"];
const MEDIA_TYPES = ["coming_soon", "youtube", "vimeo", "video", "audio"];

const strip = (p) => {
  const { edition, lang, n, edited, updated_at, id, ...content } = p;
  return content;
};

export const PageEditor = ({ page, edition, assets, onSaved }) => {
  const [draft, setDraft] = useState(strip(page));
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(strip(page)), [page]);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const saved = await updatePage(EDITION_SLUG, edition.lang, page.n, draft);
      onSaved(saved);
      toast.success(`Page ${page.n} saved (${edition.lang.toUpperCase()})`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset this page to the original seeded content?")) return;
    setBusy(true);
    try {
      const restored = await resetPage(EDITION_SLUG, edition.lang, page.n);
      onSaved(restored);
      toast.success("Page reset to original");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const preview = { ...draft, n: page.n };
  const scale = 0.5;

  return (
    <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(0, 1fr) 320px" }} data-testid="page-editor">
      <div className="flex flex-col gap-4 pb-24">
        <div className="flex items-center justify-between gap-4 sticky top-0 py-3 z-10" style={{ background: "#141414", borderBottom: "1px solid rgba(217,218,221,0.1)" }}>
          <div>
            <div className="reader-label" style={{ color: "#fff" }}>Page {page.n} · {edition.lang.toUpperCase()} {page.edited && <span style={{ color: "#ffb4c0" }}>· edited</span>}</div>
            <div className="font-head italic" style={{ fontSize: 14, opacity: 0.6 }}>{draft.title || draft.template}</div>
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-silver" onClick={reset} disabled={busy} data-testid="editor-reset-btn"><RotateCcw size={13} /> Reset</button>
            <button type="button" className="btn-bordeaux" onClick={save} disabled={busy} data-testid="editor-save-btn"><Save size={13} /> {busy ? "Saving…" : "Save"}</button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select label="Template" value={draft.template} onChange={set("template")} options={TEMPLATES} testid="editor-template" />
          <Select label="Section" value={draft.section} onChange={set("section")} options={SECTIONS} testid="editor-section" />
        </div>
        <Field label="Kicker / label" value={draft.kicker} onChange={set("kicker")} testid="editor-kicker" />
        <Field label="Title" value={draft.title} onChange={set("title")} testid="editor-title" />
        <Field label="Deck / standfirst" value={draft.deck} onChange={set("deck")} textarea testid="editor-deck" />
        <LinesField label="Body paragraphs" value={draft.body} onChange={set("body")} testid="editor-body" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pull quote" value={draft.quote} onChange={set("quote")} testid="editor-quote" />
          <Field label="Quote attribution" value={draft.quote_by} onChange={set("quote_by")} testid="editor-quote-by" />
        </div>
        <ImageField label="Main image" value={draft.image} onChange={set("image")} assets={assets} testid="editor-image" />

        <ListEditor label="Questions & answers" items={draft.qa || []} onChange={set("qa")} blank={() => ({ q: "", a: "" })} testid="editor-qa"
          render={(it, up) => (<><Field label="Question" value={it.q} onChange={(v) => up({ q: v })} /><Field label="Answer" textarea value={it.a} onChange={(v) => up({ a: v })} /></>)} />

        <ListEditor label="Buttons & media links" items={draft.ctas || []} onChange={set("ctas")} blank={() => ({ label: "", url: "", kind: "watch", media: { type: "youtube", src: "" } })} testid="editor-ctas"
          render={(it, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Label" value={it.label} onChange={(v) => up({ label: v })} />
                <Select label="Kind" value={it.kind} onChange={(v) => up({ kind: v })} options={KINDS} />
              </div>
              <Field label="Link URL (fallback / external)" value={it.url} onChange={(v) => up({ url: v })} />
              <div className="grid gap-2" style={{ gridTemplateColumns: "130px 1fr" }}>
                <Select label="Media type" value={it.media?.type || "youtube"} onChange={(v) => up({ media: { ...(it.media || {}), type: v } })} options={MEDIA_TYPES} />
                <Field label="Media source (YouTube/Vimeo URL or MP4/MP3 file) — leave empty with type “coming_soon” to show the Coming Soon poster" value={it.media?.src || ""} onChange={(v) => up({ media: { type: it.media?.type || (v ? "youtube" : "coming_soon"), src: v } })} />
              </div>
            </>
          )} />

        <ListEditor label="Photo essay images" items={draft.images || []} onChange={set("images")} blank={() => ({ src: "", caption: "" })} testid="editor-images"
          render={(it, up) => (<><ImageField label="Image" value={it.src} onChange={(v) => up({ src: v })} assets={assets} testid="editor-images-src" /><Field label="Caption" value={it.caption} onChange={(v) => up({ caption: v })} /></>)} />

        <ListEditor label="Items / cards / list entries" items={draft.items || []} onChange={set("items")} blank={() => ({ title: "", meta: "", text: "", url: "", image: "" })} testid="editor-items"
          render={(it, up) => (
            <>
              <div className="grid grid-cols-2 gap-2"><Field label="Title" value={it.title} onChange={(v) => up({ title: v })} /><Field label="Meta" value={it.meta} onChange={(v) => up({ meta: v })} /></div>
              <Field label="Text" textarea value={it.text} onChange={(v) => up({ text: v })} />
              <Field label="Link (optional)" value={it.url || ""} onChange={(v) => up({ url: v || null })} />
              <ImageField label="Image (cards)" value={it.image || ""} onChange={(v) => up({ image: v || null })} assets={assets} testid="editor-items-image" />
            </>
          )} />

        <ListEditor label="Contents entries" items={draft.toc || []} onChange={set("toc")} blank={() => ({ page: 1, label: "", section: "", image: "" })} testid="editor-toc"
          render={(it, up) => (
            <>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Page" value={String(it.page)} onChange={(v) => up({ page: parseInt(v, 10) || 1 })} />
                <Field label="Label" value={it.label} onChange={(v) => up({ label: v })} />
                <Field label="Section" value={it.section} onChange={(v) => up({ section: v })} />
              </div>
              <ImageField label="Thumbnail" value={it.image || ""} onChange={(v) => up({ image: v || null })} assets={assets} testid="editor-toc-image" />
            </>
          )} />

        <LinesField label="Credits" value={draft.credits} onChange={set("credits")} testid="editor-credits" />
        <Field label="Footnote" value={draft.note} onChange={set("note")} textarea testid="editor-note" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!draft.dark} onChange={(e) => set("dark")(e.target.checked)} data-testid="editor-dark" /> Dark background (photo essays)</label>
      </div>

      <div>
        <div className="sticky top-3">
          <div className="admin-label">Live preview</div>
          <div style={{ width: PAGE_W * scale, height: PAGE_H * scale, overflow: "hidden", boxShadow: "0 30px 60px rgba(0,0,0,0.6)" }} data-testid="editor-preview">
            <div style={{ width: PAGE_W, height: PAGE_H, transform: `scale(${scale})`, transformOrigin: "0 0" }}>
              <ReaderContext.Provider value={{ goTo: () => {}, openMedia: null, ui: edition.ui, edition, interactive: false }}>
                <MagazinePage page={preview} />
              </ReaderContext.Provider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
